import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowDown, AlertTriangle } from "lucide-react";
import { siteConfig } from "../config";
import { toast } from "sonner";

export const slug = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export function SectionHeader({ overline, title, description, id }) {
  return (
    <div className="max-w-3xl" id={id}>
      <p className="overline-label" data-testid={id ? `${id}-overline` : undefined}>
        {overline}
      </p>
      <h2 className="font-sans tracking-tight text-3xl sm:text-4xl text-paper mt-3">{title}</h2>
      {description && <p className="text-steel text-base mt-4 leading-relaxed">{description}</p>}
    </div>
  );
}

export function TechTag({ children }) {
  return <span className="tech-tag">{children}</span>;
}

export function Disclaimer({ children, tone = "cyan" }) {
  const tones = {
    cyan: "border-cyan/30 bg-cyan/5 text-pale/80",
    warn: "border-warn/40 bg-warn/5 text-warn/90",
  };
  return (
    <div className={`flex items-start gap-2 border rounded-sm px-3 py-2 text-xs font-body ${tones[tone]}`} data-testid="disclaimer-note">
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export function CountUp({ value, decimals = 0, suffix = "", prefix = "", duration = 1.4, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const numeric = typeof value === "number" && Number.isFinite(value);
  const [display, setDisplay] = useState(numeric && !reduce ? 0 : value);
  useEffect(() => {
    if (!numeric) return;
    if (!inView || reduce) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, numeric, reduce, duration]);
  return (
    <span ref={ref} className={`font-mono ${className}`}>
      {prefix}
      {numeric ? display.toFixed(decimals) : value}
      {suffix}
    </span>
  );
}

function Connector() {
  const reduce = useReducedMotion();
  return (
    <div className="relative flex justify-center py-1" aria-hidden="true">
      <div className="w-px h-8 bg-teal/40" />
      {!reduce && <span className="absolute left-1/2 -ml-[3px] top-0 w-1.5 h-1.5 rounded-full bg-cyan animate-packet-y" />}
      <ArrowDown className="absolute -bottom-1 w-3 h-3 text-teal/70" />
    </div>
  );
}

export function FlowDiagram({ layers, testId, defaultNote = "Select a node to inspect its role in the workflow." }) {
  const [sel, setSel] = useState(null);
  return (
    <div data-testid={testId}>
      <div className="space-y-0">
        {layers.map((layer, i) => (
          <div key={layer.title}>
            <div className="panel p-4">
              <p className="overline-label mb-3">{layer.title}</p>
              <div className="flex flex-wrap gap-2">
                {layer.items.map((item) => {
                  const active = sel && sel.label === item.label;
                  return (
                    <button
                      key={item.label}
                      data-testid={`${testId}-node-${slug(item.label)}`}
                      onClick={() => setSel(item)}
                      className={`font-mono text-xs px-3 py-2 rounded-sm border transition-colors duration-200 ${
                        active
                          ? "border-cyan bg-cyan/15 text-paper shadow-[0_0_12px_rgba(32,184,205,0.25)]"
                          : "border-teal/40 bg-navy text-pale/80 hover:border-cyan/60 hover:text-paper"
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {i < layers.length - 1 && <Connector />}
          </div>
        ))}
      </div>
      <div className="panel mt-4 p-4 border-l-2 border-l-cyan" data-testid={`${testId}-detail`}>
        {sel ? (
          <>
            <p className="font-mono text-sm text-cyan">{sel.label}</p>
            <p className="text-sm text-pale/80 mt-2 leading-relaxed">{item2detail(sel)}</p>
          </>
        ) : (
          <p className="text-sm text-steel">{defaultNote}</p>
        )}
      </div>
    </div>
  );
}
const item2detail = (item) => item.detail || "";

export function ChartPanel({ title, subtitle, children, testId, action }) {
  return (
    <div className="panel p-4 min-w-0" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="font-sans text-sm font-semibold text-paper">{title}</p>
          {subtitle && <p className="font-mono text-[10px] uppercase tracking-wider text-steel mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="w-full" style={{ minHeight: 220 }}>
        {children}
      </div>
    </div>
  );
}

export const CHART_COLORS = ["#20B8CD", "#137C8B", "#FFB347", "#E7F6F8", "#FF3B30", "#34C759"];

export const tooltipStyle = {
  contentStyle: {
    backgroundColor: "#0D1B2A",
    border: "1px solid rgba(32,184,205,0.35)",
    borderRadius: 4,
    fontFamily: "IBM Plex Mono, monospace",
    fontSize: 11,
    color: "#F7F9FB",
  },
  labelStyle: { color: "#20B8CD", fontFamily: "IBM Plex Mono, monospace", fontSize: 11 },
  itemStyle: { color: "#E7F6F8" },
};

export const axisProps = {
  tick: { fill: "#8A99A8", fontSize: 10, fontFamily: "IBM Plex Mono, monospace" },
  stroke: "rgba(32,184,205,0.2)",
};

export function FadeIn({ children, delay = 0, className = "" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

export function useSafeLink() {
  return (url, label, downloadName) => {
    if (!url) {
      toast(`${label} link not configured yet`, { description: "This link is hidden until a real URL is supplied in src/config.js." });
      return;
    }
    if (downloadName) {
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    if (url.startsWith("mailto:")) window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  };
}

function fallbackCopy(text, done) {
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
    done();
  } catch {
    toast.error("Copy failed — copy the URL manually");
  }
  document.body.removeChild(ta);
}

export function copyToClipboard(text, label = "Link") {
  const done = () => toast.success(`${label} copied to clipboard`);
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
  else fallbackCopy(text, done);
}

export function usePageMeta({ title, description, canonicalPath }) {
  useEffect(() => {
    const prevTitle = document.title;
    const upsert = (selector, create, attrs) => {
      let el = document.head.querySelector(selector);
      const existed = !!el;
      const prevContent = existed ? el.getAttribute("content") ?? el.getAttribute("href") : null;
      if (!el) {
        el = document.createElement(create);
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return { el, existed, prevContent };
    };
    document.title = title;
    const pageUrl = `${siteConfig.portfolioBaseUrl}${canonicalPath}`;
    const socialImage = `${siteConfig.portfolioBaseUrl}${siteConfig.socialPreviewUrl}`;
    const metas = [
      upsert('meta[name="description"]', "meta", { name: "description", content: description }),
      upsert('meta[property="og:title"]', "meta", { property: "og:title", content: title }),
      upsert('meta[property="og:description"]', "meta", { property: "og:description", content: description }),
      upsert('meta[property="og:url"]', "meta", { property: "og:url", content: pageUrl }),
      upsert('meta[property="og:image"]', "meta", { property: "og:image", content: socialImage }),
      upsert('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: title }),
      upsert('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: description }),
      upsert('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: socialImage }),
      upsert('link[rel="canonical"]', "link", { rel: "canonical", href: pageUrl }),
    ];
    return () => {
      document.title = prevTitle;
      metas.forEach(({ el, existed, prevContent }) => {
        if (!existed) {
          el.remove();
        } else if (el.tagName === "META") {
          el.setAttribute("content", prevContent ?? "");
        } else if (prevContent !== null) {
          el.setAttribute("href", prevContent);
        } else {
          el.remove();
        }
      });
    };
  }, [title, description, canonicalPath]);
}
